import type { Extensions } from "@tiptap/core";
import Blockquote from "@tiptap/extension-blockquote";
import Bold from "@tiptap/extension-bold";
import BubbleMenu from "@tiptap/extension-bubble-menu";
import BulletList from "@tiptap/extension-bullet-list";
import Code from "@tiptap/extension-code";
import CodeBlock from "@tiptap/extension-code-block";
import Document from "@tiptap/extension-document";
import Dropcursor from "@tiptap/extension-dropcursor";
import FontFamily from "@tiptap/extension-font-family";
import Gapcursor from "@tiptap/extension-gapcursor";
import HardBreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import History from "@tiptap/extension-history";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Strike from "@tiptap/extension-strike";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import Text from "@tiptap/extension-text";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { Markdown } from "tiptap-markdown";

import {
  AuthenticatedImageNode,
  BlockThread,
  InlineThread,
  LibraryImageBase64Node,
  LibraryImageNode,
  SectionTitleNode,
  YouTubeVideoNode,
} from "./custom-nodes";

type GetDocumentExtensionsParams = {
  placeholder?: string;
  history?: boolean;
};

type GetNodeExtensionsParams = {
  imgSrcMap?: Record<string, string>;
};

export const getBasicExtensions = (
  params: GetDocumentExtensionsParams = {},
): Extensions => {
  return [
    // Core extensions (required)
    Document,
    Paragraph,
    Text,

    // Formatting marks
    Bold,
    Italic,
    Strike,
    Code,

    // Nodes
    Heading,
    BulletList,
    OrderedList,
    ListItem,
    Blockquote,
    HardBreak,

    // Functionality
    Dropcursor.configure({
      width: 4,
      color: "#2d9cdb",
      class: "rounded",
    }),
    Gapcursor,

    // Content Parser
    Markdown.configure({
      transformPastedText: true,
    }),

    ...(params.history ? [History] : []),

    // Table Nodes (must be in this order)
    Table.configure({
      resizable: true,
    }),
    TableRow,
    TableHeader,
    TableCell,

    // Bubble menu (after table extensions to avoid conflicts)
    BubbleMenu,

    // List Nodes
    TaskList,
    TaskItem.configure({
      nested: true,
    }),

    // Placeholder
    Placeholder.configure({
      placeholder: params.placeholder ?? "",
    }),

    // Text Nodes
    Underline,
    Link.configure({
      openOnClick: false,
      linkOnPaste: true,
      autolink: true,
      defaultProtocol: "https",
      protocols: [],
    }),
    TextAlign.configure({
      types: ["heading", "paragraph"],
    }),
    Highlight.configure({
      multicolor: true,
    }),
    TextStyle,
    FontFamily,
  ];
};

export const getNodeExtensions = (
  params: GetNodeExtensionsParams = {},
): Extensions => {
  const libraryImageNode =
    params.imgSrcMap !== undefined
      ? LibraryImageBase64Node.configure({
          imgSrcMap: params.imgSrcMap,
        })
      : LibraryImageNode;

  return [
    ...getBasicExtensions(),
    CodeBlock,

    // Custom Nodes
    SectionTitleNode,
    YouTubeVideoNode,
    AuthenticatedImageNode,
    libraryImageNode,

    // Thread Extensions (for backend compatibility)
    InlineThread,
    BlockThread,
  ];
};
